
import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, unwrap } from "@/api/client";
import { toast } from "sonner";
import { Loader2, ShieldCheck, HeartHandshake, Mail, Smartphone, KeyRound, Sparkles } from "lucide-react";
import logoImg from "@/resources/impactVisionLogo.jpg";

const TABS = [
  { id: "password", label: "Password", icon: KeyRound },
  { id: "email",    label: "Email OTP", icon: Mail },
  { id: "mobile",   label: "Mobile OTP", icon: Smartphone },
];

export default function Login() {
  const { user, login } = useAuth();
  const [tab, setTab] = useState("password");

  if (user) return <Navigate to={user.role === "ADMIN" ? "/admin" : "/donor"} replace />;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[hsl(40,44%,98%)]">
      {/* Left hero */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[hsl(160,84%,17%)] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{backgroundImage:
          "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)",
          backgroundSize: "24px 24px"}} />
        <div className="relative">
          {/* Logo and Brand Title Header Container */}
          <div className="flex items-center gap-4 mb-4">
            <img 
              src={logoImg} 
              alt="Impact Vision Logo" 
              className="w-20 h-20 object-contain rounded-2xl shadow-md border border-white/20 bg-white p-1.5" 
            />
            <div className="font-display text-2xl font-extrabold tracking-tight">
              DRMS Impact Vision<span className="text-orange-300">.</span>
            </div>
          </div>
          <div className="overline !text-white/70">Rajalakshmi Children Foundation , Belagavi</div>
        </div>
       
        <div className="relative flex items-center gap-3 text-xs text-white/60">
          <ShieldCheck className="w-4 h-4" />
          © 2026 Rajalakshmi Children Foundation , Belagavi. All rights reserved
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-6">
            <div className="overline">Secure sign-in</div>
            <h1 className="mt-2 font-display text-4xl font-extrabold text-[hsl(160,84%,17%)]">Welcome back.</h1>
            <p className="mt-2 text-muted-foreground">
              Choose how you'd like to sign in.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-[#F0EBE0] border border-emerald-900/10 mb-5" data-testid="login-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${
                  tab === t.id
                    ? "bg-white text-emerald-900 shadow-sm"
                    : "text-emerald-900/60 hover:text-emerald-900"
                }`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "password" && <PasswordPanel onLogin={login} />}
          {tab === "email"    && <OtpPanel channel="EMAIL" placeholder="you@example.com" inputTestId="otp-email-input" />}
          {tab === "mobile"   && <OtpPanel channel="SMS"   placeholder="+91 98200 11111" inputTestId="otp-mobile-input" />}
          
          </div>
        </div>
      </div>
  );
}

function DemoCard({ title, who, mobile, tone }) {
  return (
    <div className="rounded-md border border-emerald-900/10 bg-white px-3 py-2">
      <div className="font-semibold text-emerald-900">{title}</div>
      <div className="text-muted-foreground truncate">{who}</div>
      <div className="text-[10px] mono text-emerald-800/70">{mobile}</div>
    </div>
  );
}

function PasswordPanel({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await onLogin(email.trim(), password);
      toast.success(`Welcome, ${u.fullName}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <Field label="Email">
        <input type="email" data-testid="login-email" value={email} onChange={e=>setEmail(e.target.value)} required
          className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(158,64%,40%)]"
          placeholder="you@example.com" />
      </Field>
      <Field label="Password">
        <input type="password" data-testid="login-password" value={password} onChange={e=>setPassword(e.target.value)} required
          className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(158,64%,40%)]"
          placeholder="••••••••" />
      </Field>
      <button type="submit" disabled={busy} data-testid="login-submit"
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[hsl(160,84%,17%)] px-4 py-3 text-sm font-semibold text-white hover:bg-[hsl(160,84%,13%)] transition-colors active:scale-[0.99] disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in securely
      </button>
    </form>
  );
}

function OtpPanel({ channel, placeholder, inputTestId }) {
  const { login: _ignore } = useAuth();
  const [destination, setDestination] = useState("");
  const [step, setStep] = useState("request"); // "request" | "verify"
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(null);   // demo code shown to user in demo mode
  const [expiresIn, setExpiresIn] = useState(0);
  const [resendIn, setResendIn] = useState(0);

  // Countdown ticks
  useEffect(() => {
    if (step !== "verify") return;
    const iv = setInterval(() => {
      setExpiresIn((s) => Math.max(0, s - 1));
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(iv);
  }, [step]);

  const label = channel === "EMAIL" ? "Email address" : "Mobile number (with country code)";

  const requestOtp = async () => {
    if (!destination.trim()) { toast.error(`Enter your ${channel === "EMAIL" ? "email" : "mobile number"}`); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/request", { channel, destination: destination.trim() });
      const d = unwrap(r);
      setStep("verify");
      setExpiresIn(d.expiresInSeconds || 300);
      setResendIn(d.resendInSeconds || 60);
      setDemo(d.demoMode ? d.demoCode : null);
      toast.success(d.demoMode
        ? `Demo mode: your code is ${d.demoCode}`
        : `Code sent to ${d.destination}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (code.length < 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/verify", { channel, destination: destination.trim(), code });
      const { token, user } = r.data.data;
      localStorage.setItem("drms_token", token);
      localStorage.setItem("drms_user", JSON.stringify(user));
      toast.success(`Welcome, ${user.fullName}`);
      // Force refresh so AuthContext picks up
      window.location.href = user.role === "ADMIN" ? "/admin" : "/donor";
    } catch (err) {
      toast.error(err.response?.data?.message || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid={`otp-panel-${channel.toLowerCase()}`}>
      <Field label={label}>
        <input
          data-testid={inputTestId}
          type={channel === "EMAIL" ? "email" : "tel"}
          value={destination}
          onChange={(e)=>setDestination(e.target.value)}
          disabled={step === "verify"}
          placeholder={placeholder}
          className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(158,64%,40%)] disabled:bg-slate-50 disabled:text-slate-500"
        />
      </Field>

      {step === "request" && (
        <button type="button" onClick={requestOtp} disabled={busy} data-testid={`otp-send-${channel.toLowerCase()}`}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[hsl(160,84%,17%)] px-4 py-3 text-sm font-semibold text-white hover:bg-[hsl(160,84%,13%)] transition-colors disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Send {channel === "EMAIL" ? "email" : "SMS"} code
        </button>
      )}

      {step === "verify" && (
        <>
          {demo && (
            <div className="rounded-lg border border-orange-700/20 bg-orange-50 px-3 py-2.5 text-xs text-orange-800 flex items-center gap-2" data-testid="demo-code-banner">
              <Sparkles className="w-3.5 h-3.5" />
              Demo mode • code <span className="mono font-bold text-orange-900">{demo}</span>
              <span className="text-orange-700/70">(no {channel === "EMAIL" ? "email" : "SMS"} provider configured)</span>
            </div>
          )}
          <form onSubmit={verifyOtp} className="space-y-3">
            <Field label="Enter 6-digit code">
              <input
                data-testid="otp-code-input"
                inputMode="numeric" pattern="[0-9]*" maxLength={6}
                value={code} autoFocus
                onChange={(e)=>setCode(e.target.value.replace(/\D/g,""))}
                className="w-full text-center tracking-[0.6em] mono text-2xl font-bold rounded-lg border border-border bg-white px-3.5 py-3 focus:outline-none focus:ring-2 focus:ring-[hsl(158,64%,40%)]"
                placeholder="••••••"
              />
            </Field>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="otp-expiry">
                Expires in <span className="mono font-semibold">{Math.floor(expiresIn/60)}:{String(expiresIn%60).padStart(2,"0")}</span>
              </span>
              <button
                type="button"
                disabled={resendIn > 0 || busy}
                onClick={requestOtp}
                data-testid="otp-resend"
                className="font-semibold text-emerald-800 hover:underline disabled:no-underline disabled:text-slate-400">
                {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
              </button>
            </div>
            <button type="submit" disabled={busy || code.length < 6} data-testid="otp-verify-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[hsl(160,84%,17%)] px-4 py-3 text-sm font-semibold text-white hover:bg-[hsl(160,84%,13%)] transition-colors disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Verify & sign in
            </button>
            <button type="button" onClick={() => { setStep("request"); setCode(""); setDemo(null); }}
              className="w-full text-xs text-muted-foreground hover:text-emerald-900" data-testid="otp-change-destination">
              Use a different {channel === "EMAIL" ? "email" : "mobile"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-emerald-900 tracking-wide uppercase">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}